import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingButton from '../../../components/form/LoadingButton';
import { apiPost } from '../../../utils/api';
import { showError, showSuccess } from '../../../utils/alert';
import AdvancedPhoneField from '../../../components/form/AdvancedPhoneField';
import { RECAPTCHA_SITE_KEY, isRecaptchaConfigured } from '../../../utils/captcha';
import { getIpAddress } from '../../../utils/getIpAddress';
import { getDeviceId } from '../../../api/token';

interface LoginFormData {
  phone: string;
}

export function OrganizerLoginForm() {
  const { t, i18n } = useTranslation();
  const { handleSubmit, register, formState: { errors } } = useForm<LoginFormData>();
  const [loading, setLoading] = useState(false);
  const isRTL = i18n.language === 'ar';
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [userIp, setUserIp] = useState<string>('');
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchIp = async () => {
      const ip = await getIpAddress();
      setUserIp(ip);
    };
    fetchIp();
  }, []);

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoading(true);

      if (!captchaToken) {
        showError(t('login.recaptchaRequired'));
        setLoading(false);
        return;
      }

      if (!userIp || userIp === 'unknown') {
        showError(t('login.ipDetectionFailed'));
        setLoading(false);
        return;
      }

      const deviceId = getDeviceId();
      if (!deviceId) {
        showError(t('login.deviceIdMissing'));
        setLoading(false);
        return;
      }

      const response = await apiPost('otp-code/send/organizer', {
        phone: `${data.phone}`,
        deviceId: deviceId,
        ip: userIp,
        captchaToken: captchaToken
      }) as { status: string, code: string, phone: string };

      const message = t("signup_otp");
      showSuccess(message);

      recaptchaRef.current?.reset();
      setCaptchaToken(null);

      const params = new URLSearchParams(location.search);
      const redirect = params.get("redirect");
      navigate(`/organizer/verify-otp?code=${response.code}`, { state: { phone: response.phone, redirect } });
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
      showError(err.message);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <AdvancedPhoneField
        placeholder={t('login.phonePlaceholder')}
        register={register('phone', { required: t('login.phoneRequired') })}
        error={errors.phone}
      />

      {isRecaptchaConfigured() && (
        <div className="flex justify-center">
          {/* @ts-ignore - React 19 type compatibility */}
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={handleCaptchaChange}
          />
        </div>
      )}

      {!isRecaptchaConfigured() && (
        <div className="text-red-500 text-sm text-center">
          {t('login.recaptchaNotConfigured')}
        </div>
      )}

      {loading ? (
        <LoadingButton height={64} />
      ) : (
        <button
          type="submit"
          disabled={!captchaToken || !userIp || !isRecaptchaConfigured()}
          className={`bg-primaryButton text-white px-6 py-2 h-[64px] text-xl transition w-full rounded-[8px] capitalize font-semibold ${(!captchaToken || !userIp || !isRecaptchaConfigured()) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-opacity-85'}`}
        >
          {t('send_opt')}
        </button>
      )}
    </form>
  );
}

export default function OrganizerSendOtpPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen w-screen flex flex-col bg-light overflow-hidden">
      <div className="flex flex-1 justify-center items-center p-8 md:p-16 min-h-screen">
        <div className="w-full max-w-md p-6 md:p-8">
          <h1 className="text-2xl text-center font-bold text-Black mb-2">
            {t('organizer_login.title')}
          </h1>
          <p className="text-Black font-normal text-center mb-6">
            {t('admin_login.headline')}
          </p>
          <OrganizerLoginForm />
        </div>
      </div>
    </div>
  );
}
